import * as React from 'react';

import * as Axon from '@jlekie/axon';
import * as Wellspring from '@greenheck/wellspring';

import * as Settings from '../contexts/settings';
import * as Services from '../../lib/services';
import { createWellspringServiceClient } from '../../lib/serviceClient';

export interface EntityRenderProps {
    entity: Services.ConfigurationProjectionLayout.Entity;
    onChange: (updates: Record<string, string>) => void;
    refresh: () => void;
}

export interface EntityProps {
    entityKey: string;
    layoutName: string;
    render?: (props: EntityRenderProps) => JSX.Element;
}
export function Entity({ render, entityKey, layoutName }: EntityProps) {
    const [ settings ] = Settings.useSettings();

    const [ serviceClient, setServiceClient ] = React.useState<Services.Configuration.EntityServiceClient>();
    React.useEffect(() => {
        setServiceClient(createWellspringServiceClient(Services.Configuration.EntityServiceClient, settings.servicesUrl, 'bff.cygnus.configuration.entity'));
    }, [ settings ]);

    const [ context, setContext ] = React.useState(new Services.Configuration.EntityContext());
    React.useEffect(() => {(async () => {
        if (!entityKey || !layoutName)
            return;

        await serviceClient?.init(context, entityKey, layoutName);
        setContext(context.clone());
    })().catch(err => {
        console.error(err)
    })}, [ serviceClient, entityKey, layoutName ]);

    const [ entity, setEntity ] = React.useState<Services.ConfigurationProjectionLayout.Entity>();
    React.useEffect(() => {(async () => {
        if (!serviceClient)
            return;
        if (!context.entityKey || !context.layoutName)
            return;

        const entity = await serviceClient.get(context);
        setEntity(entity);
    })().catch(err => {
        console.error(err)
    })}, [ serviceClient, context ]);

    const onChange = (updates: Record<string, string>) => {(async () => {
        const transformedUpdates: Array<Services.Configuration.EntityStateQuestion> = [];
        for (let key in updates) {
            transformedUpdates.push(new Services.Configuration.EntityStateQuestion({
                name: key,
                value: updates[key]
            }));
        }

        const entity = await serviceClient?.update(context, transformedUpdates);
        setEntity(entity);
    })()}

    const refresh = () => {(async () => {
        const entity = await serviceClient?.get(context);
        setEntity(entity);
    })()}

    return (
        <React.Fragment>
            {render && (entity ? render({
                entity,
                onChange,
                refresh
            }) : <em>Loading entity...</em>)}
        </React.Fragment>
    );
}

export const useEntityXM = (entityKey: string, layoutName: string) => {
    const [ settings ] = Settings.useSettings();

    const [ serviceClient, setServiceClient ] = React.useState<Services.Configuration.EntityServiceClient>();
    React.useEffect(() => {
        setServiceClient(createWellspringServiceClient(Services.Configuration.EntityServiceClient, settings.servicesUrl, 'bff.cygnus.configuration.entity'));
    }, [ settings ]);

    const [ coreCacheServiceClient, setCoreCacheServiceClient ] = React.useState<Services.ConfigurationCore.CacheServiceClient>();
    React.useEffect(() => {
        setCoreCacheServiceClient(createWellspringServiceClient(Services.ConfigurationCore.CacheServiceClient, settings.servicesUrl, 'nucleus.configuration.core.cache'));
    }, [ settings ]);

    const [ context, setContext ] = React.useState(new Services.Configuration.EntityContext({ entityKey, layoutName }));
    React.useEffect(() => {
        setContext(new Services.Configuration.EntityContext({ entityKey, layoutName }));
    }, [ entityKey, layoutName ])

    const [ entity, setEntity ] = React.useState<Services.ConfigurationProjectionLayout.Entity>();
    React.useEffect(() => {(async () => {
        if (!serviceClient)
            return;
        if (!coreCacheServiceClient)
            return;
        if (!context.entityKey || !context.layoutName)
            return;

        const entity = await serviceClient.get(context);
        setEntity(entity);

        const coreEntity = await coreCacheServiceClient?.load(context.entityKey);
        console.log('CORE ENTITY (layout)', JSON.parse(JSON.stringify(coreEntity)));
    })().catch(err => {
        console.error(err)
    })}, [ serviceClient, coreCacheServiceClient, context ]);

    // const onUpdated = React.useCallback(() => {
        
    // }, [ entity ]);

    const update = async (updates: Record<string, string>) => {
        const transformedUpdates: Array<Services.Configuration.EntityStateQuestion> = [];
        for (let key in updates) {
            transformedUpdates.push(new Services.Configuration.EntityStateQuestion({
                name: key,
                value: updates[key]
            }));
        }

        const actionContext = new Wellspring.ActionContext();
        const entity = await serviceClient?.update(actionContext, context, transformedUpdates);

        // const protocol = new Axon.EntanglementProtocol();
        // const data = actionContext.metadata.get('debug');
        // const debug = protocol.read(data, reader => {
        //     const modelHeader = reader.readModelHeader();

        //     const updateResults = new Services.ConfigurationCore.UpdateResult();
        //     updateResults.read(reader, modelHeader);

        //     return updateResults;
        // });
        // console.log(debug)
        
        setEntity(entity);

        const coreEntity = await coreCacheServiceClient?.load(context.entityKey);
        console.log('CORE ENTITY (layout)', JSON.parse(JSON.stringify(coreEntity)));
    }
    const refresh = async () => {
        const entity = await serviceClient?.get(context);
        setEntity(entity);

        const coreEntity = await coreCacheServiceClient?.load(context.entityKey);
        console.log('CORE ENTITY (layout)', JSON.parse(JSON.stringify(coreEntity)));
    }

    return [ entity, update, refresh ] as const;
}